import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchEncounter } from '../api/encounters';
import type { EncounterParticipant } from '../api/types';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';
import { formatDateTime, formatDecimal, formatDuration, formatNumber, formatPercent } from '../utils/format';
import {
  getActiveServerSlug,
  serverBossMetricPath,
  serverCharacterPath,
  serverGuildPath,
  serverReportPath,
  serverDisplayName,
} from '../utils/siteContext';

function participantDamage(participant: EncounterParticipant) {
  return participant.metrics?.displayDamage ?? participant.damage;
}

function participantDps(participant: EncounterParticipant) {
  return participant.parseRecord?.dps ?? participant.dps;
}

function participantHits(participant: EncounterParticipant) {
  return participant.metrics?.totalHitCount ?? participant.hitCount;
}

function participantHealing(participant: EncounterParticipant) {
  return participant.metrics?.totalHealing ?? participant.healingParseRecord?.totalHealing ?? participant.healing ?? 0;
}

function participantHps(participant: EncounterParticipant) {
  return participant.metrics?.hps ?? participant.healingParseRecord?.hps ?? participant.hps ?? 0;
}

function participantHeals(participant: EncounterParticipant) {
  return participant.metrics?.totalHealCount ?? participant.healingParseRecord?.totalHealCount ?? participant.healCount ?? 0;
}

function classShortName(className: string | null | undefined) {
  const normalized = className?.trim();
  if (!normalized) return '???';

  const shorthand: Record<string, string> = {
    Bard: 'BRD',
    Beastlord: 'BST',
    Berserker: 'BER',
    Cleric: 'CLR',
    Druid: 'DRU',
    Enchanter: 'ENC',
    Magician: 'MAG',
    Monk: 'MNK',
    Necromancer: 'NEC',
    Paladin: 'PAL',
    Ranger: 'RNG',
    Rogue: 'ROG',
    'Shadow Knight': 'SHD',
    Shaman: 'SHM',
    Warrior: 'WAR',
    Wizard: 'WIZ',
  };

  return shorthand[normalized] ?? normalized.slice(0, 3).toUpperCase();
}

function topAbilityRows(participants: EncounterParticipant[], metric: 'damage' | 'healing') {
  const rows = new Map<string, { ability: string; amount: number; events: number; users: Set<string> }>();

  for (const participant of participants) {
    const amount = metric === 'damage' ? participantDamage(participant) : participantHealing(participant);
    const events = metric === 'damage' ? participantHits(participant) : participantHeals(participant);
    if (amount <= 0 && events <= 0) {
      continue;
    }

    const ability = (metric === 'damage' ? participant.topAbility : participant.topHealAbility) || 'Unknown';
    const existing = rows.get(ability) ?? { ability, amount: 0, events: 0, users: new Set<string>() };
    existing.amount += amount;
    existing.events += events;
    existing.users.add(participant.name);
    rows.set(ability, existing);
  }

  return [...rows.values()]
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 8)
    .map((row) => ({ ...row, userCount: row.users.size }));
}

function percentileBadgeClass(percentile: number | null | undefined) {
  if (percentile === null || percentile === undefined) return 'percentile-badge percentile-badge--none';
  if (percentile >= 100) return 'percentile-badge percentile-badge--gold';
  if (percentile >= 99) return 'percentile-badge percentile-badge--pink';
  if (percentile >= 95) return 'percentile-badge percentile-badge--orange';
  if (percentile >= 75) return 'percentile-badge percentile-badge--purple';
  if (percentile >= 50) return 'percentile-badge percentile-badge--blue';
  if (percentile >= 25) return 'percentile-badge percentile-badge--green';
  return 'percentile-badge percentile-badge--gray';
}

function ParseValue({ percentile }: { percentile: number | null | undefined }) {
  if (percentile === null || percentile === undefined) {
    return <span className="text-body-secondary">N/A</span>;
  }

  return <span className={percentileBadgeClass(percentile)}>{formatDecimal(percentile, 0)}</span>;
}

function RankValue({ rank, totalParses }: { rank: number | null | undefined; totalParses: number | null | undefined }) {
  if (!rank) {
    return <span className="text-body-secondary">Unranked</span>;
  }

  return (
    <span className="tabular-nums">
      {formatNumber(rank)}
      {totalParses ? <span className="text-body-secondary"> / {formatNumber(totalParses)}</span> : null}
    </span>
  );
}

function linkedParticipantName(participant: EncounterParticipant, activeServerSlug: string) {
  return participant.character ? (
    <Link className="link-light text-decoration-none" to={serverCharacterPath(activeServerSlug, participant.character)}>{participant.name}</Link>
  ) : participant.name;
}

function EncounterOverviewItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="report-overview-item">
      <small>{label}</small>
      <strong>{value}</strong>
    </div>
  );
}

function DamageTable({
  participants,
  activeServerSlug,
}: {
  participants: EncounterParticipant[];
  activeServerSlug: string;
}) {
  return (
    <div className="card report-table-card">
      <div className="card-body">
        <h2 className="h5 mb-3">Damage Done</h2>
        <div className="table-responsive">
          <table className="table table-dark table-hover align-middle mb-0">
            <thead>
              <tr>
                <th scope="col">Character</th>
                <th scope="col">Class</th>
                <th scope="col">Top Ability</th>
                <th scope="col" className="text-end">Damage</th>
                <th scope="col" className="text-end">Pet</th>
                <th scope="col" className="text-end">DPS</th>
                <th scope="col" className="text-end">Hits</th>
                <th scope="col" className="text-end">Crit</th>
                <th scope="col" className="text-end">Parse</th>
                <th scope="col" className="text-end">Rank</th>
              </tr>
            </thead>
            <tbody>
              {participants.map((participant) => (
                <tr key={participant.id}>
                  <td>
                    <div className="fw-semibold">{linkedParticipantName(participant, activeServerSlug)}</div>
                    {participant.ownerName ? (
                      <div className="small text-body-secondary">
                        Pet of {participant.ownerCharacter ? (
                          <Link className="link-secondary text-decoration-none" to={serverCharacterPath(activeServerSlug, participant.ownerCharacter)}>{participant.ownerName}</Link>
                        ) : participant.ownerName}
                      </div>
                    ) : null}
                  </td>
                  <td><span className="class-shorthand">{classShortName(participant.character?.className)}</span></td>
                  <td>{participant.topAbility ?? 'Unknown'}</td>
                  <td className="text-end tabular-nums">{formatNumber(participantDamage(participant))}</td>
                  <td className="text-end tabular-nums">{formatNumber(participant.metrics?.petDamage ?? 0)}</td>
                  <td className="text-end tabular-nums">{formatDecimal(participantDps(participant), 1)}</td>
                  <td className="text-end tabular-nums">{formatNumber(participantHits(participant))}</td>
                  <td className="text-end tabular-nums">{formatPercent(participant.critPercent)}</td>
                  <td className="text-end"><ParseValue percentile={participant.parseRecord?.percentile} /></td>
                  <td className="text-end"><RankValue rank={participant.parseRecord?.rank} totalParses={participant.parseRecord?.totalParses} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function HealingTable({
  participants,
  activeServerSlug,
}: {
  participants: EncounterParticipant[];
  activeServerSlug: string;
}) {
  return (
    <div className="card report-table-card">
      <div className="card-body">
        <h2 className="h5 mb-3">Healing Done</h2>
        <div className="table-responsive">
          <table className="table table-dark table-hover align-middle mb-0">
            <thead>
              <tr>
                <th scope="col">Character</th>
                <th scope="col">Class</th>
                <th scope="col">Top Ability</th>
                <th scope="col" className="text-end">Healing</th>
                <th scope="col" className="text-end">Pet Heal</th>
                <th scope="col" className="text-end">HPS</th>
                <th scope="col" className="text-end">Heals</th>
                <th scope="col" className="text-end">Parse</th>
                <th scope="col" className="text-end">Rank</th>
              </tr>
            </thead>
            <tbody>
              {participants.length === 0 ? (
                <tr><td colSpan={9} className="text-body-secondary py-3">No healing parses recorded for this encounter yet.</td></tr>
              ) : participants.map((participant) => (
                <tr key={`healing-${participant.id}`}>
                  <td>
                    <div className="fw-semibold">{linkedParticipantName(participant, activeServerSlug)}</div>
                    {participant.ownerName ? (
                      <div className="small text-body-secondary">
                        Pet of {participant.ownerCharacter ? (
                          <Link className="link-secondary text-decoration-none" to={serverCharacterPath(activeServerSlug, participant.ownerCharacter)}>{participant.ownerName}</Link>
                        ) : participant.ownerName}
                      </div>
                    ) : null}
                  </td>
                  <td><span className="class-shorthand">{classShortName(participant.character?.className)}</span></td>
                  <td>{participant.topHealAbility ?? 'Unknown'}</td>
                  <td className="text-end tabular-nums">{formatNumber(participantHealing(participant))}</td>
                  <td className="text-end tabular-nums">{formatNumber(participant.metrics?.petHealing ?? 0)}</td>
                  <td className="text-end tabular-nums">{formatDecimal(participantHps(participant), 1)}</td>
                  <td className="text-end tabular-nums">{formatNumber(participantHeals(participant))}</td>
                  <td className="text-end"><ParseValue percentile={participant.healingParseRecord?.percentile} /></td>
                  <td className="text-end"><RankValue rank={participant.healingParseRecord?.rank} totalParses={participant.healingParseRecord?.totalParses} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function EncounterDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [activeMetric, setActiveMetric] = useState<'damage' | 'healing'>('damage');

  const query = useQuery({
    queryKey: ['encounter', id],
    queryFn: () => fetchEncounter(id ?? ''),
    enabled: Boolean(id)
  });

  const activeServerSlug = getActiveServerSlug(window.location.pathname);

  if (!id) {
    return (
      <div className="content-wrapper py-4">
        <ErrorState title="Invalid encounter" error={new Error('Missing encounter id')} />
      </div>
    );
  }

  if (query.isLoading) {
    return <div className="content-wrapper py-4"><LoadingState label="Loading encounter" /></div>;
  }

  if (query.isError) {
    return <div className="content-wrapper py-4"><ErrorState title="Could not load encounter" error={query.error} /></div>;
  }

  const encounter = query.data;
  if (!encounter) {
    return null;
  }

  const encounterServerSlug = encounter.server?.slug ?? activeServerSlug;
  const serverName = encounter.server?.name ?? serverDisplayName(encounterServerSlug);
  const rankedParticipants = [...(encounter.participants ?? [])].sort((a, b) => participantDamage(b) - participantDamage(a));
  const healingParticipants = [...(encounter.participants ?? [])]
    .filter((participant) => participantHealing(participant) > 0 || participant.healingParseRecord)
    .sort((a, b) => participantHealing(b) - participantHealing(a));
  const activeParticipants = activeMetric === 'damage' ? rankedParticipants : healingParticipants;
  const abilityRows = topAbilityRows(activeParticipants, activeMetric);
  const sourceLabel = encounter.guild ? (
    <Link className="link-light text-decoration-none" to={serverGuildPath(encounterServerSlug, encounter.guild)}>
      &lt;{encounter.guild.name}&gt;
    </Link>
  ) : 'Personal Logs';

  return (
    <>
      <section className="page-hero">
        <div className="content-wrapper py-4">
          <h1 className="display-6 fw-semibold mb-0">{encounter.targetName}</h1>
          <span className="page-hero__server d-block">{serverName}</span>
          <p className="fs-3 mb-0">{encounter.zone}</p>
        </div>
      </section>

      <div className="page-subnav">
        <div className="content-wrapper">
          <ul className="nav nav-tabs secondary-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${activeMetric === 'damage' ? 'active' : ''}`}
                type="button"
                onClick={() => setActiveMetric('damage')}
              >
                Damage
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeMetric === 'healing' ? 'active' : ''}`}
                type="button"
                onClick={() => setActiveMetric('healing')}
              >
                Healing
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div className="content-wrapper page-main">
        <div className="report-overview-strip mb-4">
          <EncounterOverviewItem label="Guild" value={sourceLabel} />
          <EncounterOverviewItem label="Date" value={formatDateTime(encounter.startedAt)} />
          <EncounterOverviewItem label="Duration" value={formatDuration(encounter.durationSec)} />
          <EncounterOverviewItem label="Total Damage" value={formatNumber(encounter.totalDamage)} />
          <EncounterOverviewItem label="Raid DPS" value={formatDecimal(encounter.dps, 1)} />
          <EncounterOverviewItem label="Total Healing" value={formatNumber(encounter.totalHealing ?? 0)} />
          <EncounterOverviewItem label="Raid HPS" value={formatDecimal(encounter.hps ?? 0, 1)} />
          <EncounterOverviewItem label="Players" value={formatNumber(encounter.playerCount)} />
          <EncounterOverviewItem label="POVs" value={formatNumber(encounter.contributorCount)} />
          <EncounterOverviewItem
            label="Report"
            value={encounter.reportId ? (
              <Link className="link-light text-decoration-none" to={serverReportPath(encounterServerSlug, encounter.reportId)}>View report</Link>
            ) : <span className="text-body-secondary">None</span>}
          />
          <EncounterOverviewItem
            label="Boss"
            value={encounter.raidBoss ? (
              <Link className="link-light text-decoration-none" to={serverBossMetricPath(encounterServerSlug, encounter.raidBoss.slug ?? encounter.raidBoss.id, 'DAMAGE')}>{encounter.raidBoss.name}</Link>
            ) : <span className="text-body-secondary">Unmatched</span>}
          />
        </div>

        {encounter.rankingEligible === false ? (
          <div className="alert alert-warning" role="alert">
            <div className="fw-semibold">This encounter is not ranking eligible.</div>
            <div>{encounter.rankingIneligibleReason ?? 'No reason was provided by the API.'}</div>
          </div>
        ) : null}

        <div className="row g-4">
          <div className="col-12 col-xl-9">
            <div className="d-grid gap-4">
              {activeMetric === 'damage' ? (
                <DamageTable participants={rankedParticipants} activeServerSlug={activeServerSlug} />
              ) : (
                <HealingTable participants={healingParticipants} activeServerSlug={activeServerSlug} />
              )}
            </div>
          </div>

          <div className="col-12 col-xl-3">
            <div className="card report-table-card mb-4">
              <div className="card-body">
                <h2 className="h5 mb-3">{activeMetric === 'damage' ? 'Top Damage Abilities' : 'Top Healing Abilities'}</h2>
                <div className="list-group list-group-flush">
                  {abilityRows.map((row) => (
                    <div className="list-group-item px-0" key={row.ability}>
                      <div className="d-flex justify-content-between gap-3">
                        <div className="fw-semibold">{row.ability}</div>
                        <div className="tabular-nums">{formatNumber(row.amount)}</div>
                      </div>
                      <div className="small text-body-secondary">
                        {formatNumber(row.events)} {activeMetric === 'damage' ? 'hits' : 'heals'} · {formatNumber(row.userCount)} participants
                      </div>
                    </div>
                  ))}
                  {abilityRows.length === 0 ? <div className="list-group-item px-0 text-body-secondary">No participant abilities recorded.</div> : null}
                </div>
              </div>
            </div>

            <div className="card report-table-card">
              <div className="card-body">
                <h2 className="h5 mb-3">Upload Sources</h2>
                <div className="list-group list-group-flush">
                  {encounter.submissions.map((submission) => (
                    <div className="list-group-item px-0" key={submission.id}>
                      <div className="fw-semibold">{submission.uploadedBy?.username ?? 'Unknown user'}</div>
                      <div className="small text-body-secondary">
                        Uploaded {formatDateTime(submission.receivedAt)}
                      </div>
                    </div>
                  ))}
                  {encounter.submissions.length === 0 ? (
                    <div className="list-group-item px-0 text-body-secondary">No upload sources recorded.</div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
